import { ReportReason, type PublicConfig } from "@cfg/api-contract";
import { ConfiguratorProvider, Viewport, buildConfiguratorSearch, useDesign, useMesh, useParams } from "@cfg/configurator-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams as useRouteParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { getKernelClient } from "../kernel.ts";
import { paramRows } from "../param-rows.ts";
import { displayTitle, shortDate } from "../titles.ts";
import { PageHead } from "./PageHead.tsx";
import { DefaultEmptyState } from "./defaults.tsx";

/** One published configuration: a live preview, what it fits, who printed it, its params, and a way into the configurator. */
export function CommunityItemPage() {
  const { id = "" } = useRouteParams();
  const { api, brand, copy, registry } = useApp();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const q = useQuery({ queryKey: ["public", id], queryFn: () => api!.getPublic(id), enabled: !!api });
  // The configurator resolves bundles through the registry; prefer that so the preview loads the same file.
  const resolved = useQuery({
    queryKey: ["resolve", q.data?.designId, q.data?.version],
    queryFn: () => registry.resolve(q.data!.designId, q.data!.version).catch(() => null),
    enabled: !!q.data,
  });

  if (!api) return <Empty title={copy("community.title")} body="This deployment has no API configured." />;
  if (q.isLoading) return <p className="muted page">Loading…</p>;
  if (q.isError || !q.data)
    return (
      <section className="page page-community-item page-community-missing">
        <Empty
          title={copy("community.unavailable")}
          body={copy("community.unavailableBody")}
          action={
            <a className="cfg-btn cfg-btn-primary" href="/community">
              {copy("community.browse")}
            </a>
          }
        />
      </section>
    );
  if (resolved.isLoading) return <p className="muted page">Loading…</p>;
  const p = q.data;
  const bundleUrl = resolved.data?.bundleUrl ?? p.bundleUrl;
  return (
    <ConfiguratorProvider client={getKernelClient()} designKey={`${p.designId}@${p.version}`} bundleUrl={bundleUrl} initialParams={p.params}>
      <CommunityItem item={p} />
    </ConfiguratorProvider>
  );
}

function CommunityItem({ item: p }: { item: PublicConfig }) {
  const { api, auth, brand, copy, registry } = useApp();
  const user = useAuthUser(auth);
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: ["catalog", brand.config.id], queryFn: () => registry.catalog() });
  const { description, spec } = useDesign();
  const { params } = useParams();
  const { mesh } = useMesh();
  const [confirmMsg, setConfirmMsg] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState<ReportReason>("wrong-fit");
  const [note, setNote] = useState("");
  const [reportMsg, setReportMsg] = useState<string | null>(null);

  const entry = catalog.data?.find((d) => d.id === p.designId);
  const designTitle = entry?.title ?? (description?.meta.title ? displayTitle(String(description.meta.title)) : p.designId);
  const openHref = `/design/${encodeURIComponent(p.designId)}${buildConfiguratorSearch({ version: p.version, params: p.params })}`;
  const scene = brand.scenes[brand.config.scene];
  // Once the pinned version is loaded, show its effective values (saved params over defaults) in its own order.
  const rows = paramRows(spec, description ? params : p.params, copy("community.on"), copy("community.off"));

  const confirm = async () => {
    if (!api) return;
    if (!user) return void auth.signIn();
    try {
      const r = await api.confirmPublic(p.id);
      setConfirmMsg(copy("community.confirmed"));
      qc.setQueryData(["public", p.id], { ...p, confirmCount: r.confirmCount });
    } catch (err) {
      setConfirmMsg((err as Error).message);
    }
  };
  const report = async () => {
    if (!api) return;
    try {
      await api.reportPublic(p.id, { reason, note: note.trim() || undefined });
      setReportMsg(copy("community.reported"));
      setReporting(false);
    } catch (err) {
      setReportMsg((err as Error).message);
    }
  };

  return (
    <section className="page page-community-item">
      <PageHead
        eyebrow={copy("community.eyebrow")}
        title={p.name}
        sub={
          <>
            <a className="community-item-design" href={`/design/${encodeURIComponent(p.designId)}`}>
              {designTitle}
            </a>
            {" · "}
            <span className="community-item-author">{copy("community.by", { name: p.authorName })}</span>
            {" · "}
            <time className="community-item-date" dateTime={p.updatedAt}>
              {shortDate(p.updatedAt)}
            </time>
          </>
        }
      />

      <div className="community-item-layout">
        <figure className="community-item-preview" aria-label={copy("community.preview")}>
          {!mesh && entry?.thumbnailUrl && <img className="community-item-thumb" src={entry.thumbnailUrl} alt="" />}
          {scene && <Viewport preset={scene} modelColor={brand.config.theme.colors.model} showLabels={false} />}
        </figure>

        <div className="community-item-body">
          {p.fits.length > 0 && (
            <ul className="cfg-fit-list community-fits">
              {p.fits.map((f) => (
                <li key={f.ref} className="cfg-fit-chip">
                  <a href={`/community?fit=${encodeURIComponent(f.ref)}`}>{copy("fits.chip", { label: brand.config.fitTags.label, ref: f.ref })}</a>
                  {f.name && <span>{f.name}</span>}
                </li>
              ))}
            </ul>
          )}

          {(p.printed || p.confirmCount > 0) && (
            <p className="community-signals">
              {p.printed && <span className="version-pill">✓ {copy("community.printed")}</span>}
              {p.confirmCount > 0 && <span className="version-pill">{copy("community.confirms", { count: p.confirmCount })}</span>}
            </p>
          )}

          {p.notes && <p className="community-notes">{p.notes}</p>}

          <div className="community-actions">
            <a className="cfg-btn cfg-btn-primary" href={openHref}>
              {copy("community.open")}
            </a>
            <button type="button" className="cfg-btn" onClick={confirm}>
              {copy("community.confirm")}
            </button>
          </div>
          {confirmMsg && (
            <p className="muted small community-confirm-msg" role="status">
              {confirmMsg}
            </p>
          )}

          {rows.length > 0 && (
            <>
              <h2 className="community-params-title">
                {copy("community.params")} <span className="version-pill community-item-version">v{p.version}</span>
              </h2>
              <table className="community-params">
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.name} className={r.isDefault ? "is-default" : undefined}>
                      <th scope="row">{r.label}</th>
                      <td>
                        {r.value}
                        {r.unit && <span className="community-param-unit"> {r.unit}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {user && (
        <footer className="community-item-foot">
          <button type="button" className="link community-report-link" aria-expanded={reporting} onClick={() => setReporting((r) => !r)}>
            {copy("community.report")}
          </button>
          {reporting && (
            <form
              className="community-report"
              onSubmit={(e) => {
                e.preventDefault();
                void report();
              }}
            >
              <select className="cfg-input" value={reason} onChange={(e) => setReason(e.target.value as ReportReason)} aria-label={copy("community.reportReason")}>
                {ReportReason.options.map((r) => (
                  <option key={r} value={r}>
                    {r.replace("-", " ")}
                  </option>
                ))}
              </select>
              <input className="cfg-input" maxLength={300} aria-label={copy("community.reportDetails")} placeholder={copy("community.reportDetails")} value={note} onChange={(e) => setNote(e.target.value)} />
              <button type="submit" className="cfg-btn">
                {copy("community.report")}
              </button>
            </form>
          )}
          {reportMsg && (
            <p className="muted small" role="status">
              {reportMsg}
            </p>
          )}
        </footer>
      )}
    </section>
  );
}
